import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaAngleLeft, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { computeBranding } from '../utils/useAgentDetails';

function SignInModal(props) {
  const [email, setEmail] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState(Array(6).fill(''));
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const emailRef = useRef(null);
  const codeRefs = useRef([]);
  const resetModalState = () => {
    setEmail('');
    setIsCodeSent(false);
    setCode(Array(6).fill(''));
    setIsCodeValid(true);
    setErrorMessage('');
    setIsLoading(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleSignIn = async () => {
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    trackEvent('click_sign_in_submit');
  
    setIsLoading(true);
    setErrorMessage('');
    console.log('props.agentDetails.landing_page_slug: ', props.agentDetails.landing_page_slug);
    console.log('props.agentDetails.landing_background_url: ', props.agentDetails.landing_background_url);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email`,
        {
          email: email,
          agent_slug: props.agentDetails.landing_page_slug,
        },
        {
          'Content-Type': 'application/json',
        }
      );
      console.log('Email sent:', response.data);
      setIsCodeSent(true);
      setTimeout(() => codeRefs.current[0].focus(), 100); // Focus on the first input after state update

      // Fire save-settings with the email used to request the code
      try {
        await handleTokenAndPostRequest(
          `${window.api_host}/save-settings`,
          {
            agent_slug: props.agentDetails.landing_page_slug,
            event_type: 'request_sign_in_code',
            email: email,
          },
          { 'Content-Type': 'application/json' }
        );
      } catch (err) {
        console.error('Error saving settings on code request', err);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Failed to send email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email`,
        {
          email: email,
          agent_slug: props.agentDetails.landing_page_slug,
        },
        {
          'Content-Type': 'application/json',
        }
      );
      console.log('Code resent:', response.data);
      setIsCodeValid(true);
      setErrorMessage('');
      setCode(Array(6).fill('')); // Reset the code input
      setTimeout(() => codeRefs.current[0].focus(), 100); // Focus on the first input after resetting
    } catch (error) {
      console.error('Error resending code:', error);
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeInput = (value, index) => {
    if (value.length > 1) {
      const newCode = [...code];
      value.split('').forEach((char, idx) => {
        if (index + idx < 6) {
          newCode[index + idx] = char;
        }
      });
      setCode(newCode);
      const nextIndex = index + value.length;
      if (nextIndex < 6) {
        codeRefs.current[nextIndex].focus();
      }
    } else {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Focus on the next input element
      if (value !== '' && index < 5) {
        codeRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCodeSent) {
        validateCode();
      } else {
        handleSignIn();
      }
    } else if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      codeRefs.current[index - 1].focus();
    }
  };

  useEffect(() => {
    const allDigitsFilled = code.every(digit => digit !== '');
    if (allDigitsFilled) {
      validateCode();
    }
  }, [code]);

  const validateCode = async () => {
    trackEvent('submit_sign_in_code');
    setIsLoading(true);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/validate-code`,
        {
          email: email,
          code: code.join(''),
          agent_slug: props.agentDetails.landing_page_slug,
        },
        {
          'Content-Type': 'application/json',
        }
      );
      if (response.data.valid) {
        trackEvent('sign_in_success');
        console.log('Valid code');
        localStorage.setItem('jwt_token', response.data.token);
        resetModalState();
        props.onHide();
        
        if (props.onSuccess) {
          props.onSuccess(email); // Call the analysis function after a successful login
        }
        setIsCodeValid(true);
        window.location.reload();
        
      } else {
        console.log('Invalid code');
        setIsCodeValid(false);
        setErrorMessage('Invalid code. Please try again.');
      }
    } catch (error) {
      console.error('Error validating code:', error);
      setErrorMessage('Failed to validate code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.show && emailRef.current) {
      emailRef.current.focus();
    }
  }, [props.show]);

  useEffect(() => {
    if (!props.show) {
      resetModalState();
    }
  }, [props.show]);

  const { accentColor, buttonTextColor, isLight, iconColor } = computeBranding(props.agentDetails) || {};

  return (
    <Modal 
      top
      fullscreen="xl-down"
      style={{ zIndex: 9999 }}
      show={props.show} onHide={props.onHide} onExited={resetModalState}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
      <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>
        {isCodeSent ? (
            <>
            <Button
                variant="link"
                onClick={() => {
                setIsCodeSent(false);
                setErrorMessage('');
                setCode(Array(6).fill(''));
                }}
                disabled={isLoading}
                style={{ margin: '0px', marginBottom: '6px', padding: '0px', color: props.buttonColour, fontSize: '1.2rem' }}
            >
                <FaAngleLeft style={{color: '#9ca3af'}} />
            </Button>
            <span>Enter Code</span>
            </>
        ) : 'Sign In'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '16px 24px' }}>
        {isCodeSent ? (
          <>
            <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6 }}>Email was sent to <b style={{ color: '#111' }}>{email}</b>. Make sure to check your spam or promotions folder if you can't find it.</p>            
            <p style={{ fontSize: '0.88rem', color: '#6b7280' }}>Enter the 6-digit code sent to your email.</p>
            <Row className="justify-content-center">
              {code.map((digit, index) => (
                <Col key={index} xs="auto" style={{ padding: '3px' }}>
                  <Form.Control
                    inputMode="numeric"               
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeInput(e.target.value, index)}
                    ref={(el) => (codeRefs.current[index] = el)}
                    disabled={isLoading}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData('text');
                      handleCodeInput(paste, index);
                      e.preventDefault();
                    }}
                    style={{ height: '64px', width: '46px', textAlign: 'center', margin: '0px', fontSize: '1.6rem', borderRadius: 10, border: '1px solid #e5e7eb', fontWeight: 600 }}
                  />
                </Col>
              ))}
            </Row>
            { !isCodeValid && errorMessage !== '' && <Alert variant="danger" style={{ marginTop: '16px', marginBottom: '0px', borderRadius: 10, fontSize: '0.88rem' }}>{errorMessage}</Alert> }            
          </>
        ) : (
          <>
            <p style={{ marginBottom: '16px', color: '#6b7280', lineHeight: 1.6, fontSize: '0.88rem' }}>Create a free account to unlock the full property analysis and keep track of your properties over time.</p>
            <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: '20px' }}>
              <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <FaCheckCircle style={{ color: iconColor, marginRight: '10px', marginTop: '3px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: '#374151' }}>Save and manage your property valuations</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <FaCheckCircle style={{ color: iconColor, marginRight: '10px', marginTop: '3px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: '#374151' }}>Upload photos to improve AI estimates</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <FaCheckCircle style={{ color: iconColor, marginRight: '10px', marginTop: '3px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: '#374151' }}>Add notes and track changes over time</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                <FaCheckCircle style={{ color: iconColor, marginRight: '10px', marginTop: '3px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: '#374151' }}>Get notified when your property value changes</span>
              </li>
            </ul>
            <Form>
              <Form.Group controlId="formEmail">
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  ref={emailRef}
                  disabled={isLoading}
                  onKeyDown={handleKeyDown}
                  style={{ borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 14px', fontSize: '0.88rem' }}
                />
              </Form.Group>
              {errorMessage && errorMessage !== '' && <Alert variant="danger" style={{ marginTop: '16px', marginBottom: '0px', borderRadius: 10, fontSize: '0.88rem' }}>{errorMessage}</Alert>}
            </Form>
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', textAlign: 'center', marginBottom: 0 }}>Free forever. No spam. Unsubscribe anytime.</p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ border: 'none', paddingTop: 8, paddingBottom: 20, paddingLeft: 24, paddingRight: 24 }}>
        {isCodeSent ? (
          <>
            <Button variant="link" onClick={handleResendCode} disabled={isLoading} style={{ width: '100%', fontSize: '0.88rem', color: '#6b7280', textDecoration: 'none' }}>Resend Code</Button>          
            <Button variant="primary" onClick={validateCode} disabled={isLoading} style={{ 
                width: '100%', 
                backgroundColor: accentColor, 
                borderColor: isLight ? '#222' : accentColor, 
                color: buttonTextColor,
                borderRadius: 12,
                padding: '12px 0',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: `0 4px 16px ${accentColor}30`,
              }}>
              {isLoading ? <><Spinner animation="border" size="sm" /> <span>Create Account</span></> : 'Create Account'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={handleSignIn} disabled={isLoading} style={{ 
                width: '100%', 
                backgroundColor: accentColor, 
                borderColor: isLight ? '#222' : accentColor, 
                color: buttonTextColor,
                borderRadius: 12,
                padding: '12px 0',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: `0 4px 16px ${accentColor}30`,
              }}>
              {isLoading ? <><Spinner animation="border" size="sm" /> <span>Sign In</span></> : 'Sign In'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default SignInModal;